'use client'

import { useState } from 'react'
import { useSearchParams, useTranslations } from '@/lib/next-shim'

import { useUpdateMyInfosMutation } from '@/core/graphql/hooks'
import { useNavigateOnce } from '@/hooks/useNavigateOnce'
import { sanitizeCallbackUrl } from '@/utils/auth'

import { CompleteProfileFormData } from './completeProfileForm'

/**
 * Persists the completed profile. On success it hands off to `navigateToCallback`
 * (shared with the redirect guards so the leave happens exactly once); on failure
 * it surfaces a message for the page to render.
 */
export function useSubmitCompleteProfile() {
  const [updateMyInfos, { loading: saving }] = useUpdateMyInfosMutation()
  const navigate = useNavigateOnce()
  const searchParams = useSearchParams()
  const [saveError, setSaveError] = useState<string | null>(null)
  const t = useTranslations()

  const callbackUrl = sanitizeCallbackUrl(searchParams?.get('callbackUrl'))

  const onSubmit = async (formData: CompleteProfileFormData) => {
    setSaveError(null)
    await updateMyInfos({
      variables: {
        payload: {
          birthday: formData.birthday,
          gender: formData.gender,
          ...(formData.phone ? { phone: formData.phone } : {}),
          ...(formData.secu ? { secu: formData.secu } : {}),
        },
      },
      onCompleted: () => navigate(callbackUrl),
      onError: (error) => {
        setSaveError(error.message || t('common.tryAgain'))
      },
    })
  }

  return { onSubmit, saving, saveError }
}
